<template>
  <div>
    <div class="grid bg-white">
      <div class="row grid-body">
        <div class="col-md-12 header-row">
          <h3 class="heading semi-bold">Fabric analytics
            <span class="fabric-calculation-info-container bg-primary no-inherit-styles">
                <FabricCalculationInfo>
                  <template #title>This table shows the Fabric Analytics.</template>
                  <template #content>
                      <div class="calculation-info-container">
                          <div class="title">Total Usage</div>
                          <div class="description">The total length of fabric used for cutting shirts within a specified period.</div>
                          <code>
                              $result = sum($totalLengthCutOfFabricRoll);
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Avg. Daily Usage</div>
                          <div class="description">The total length of fabric reserved for shirt orders.</div>
                          <code>
                              $numberOfDays = ($dateTo - $dateFrom) + 1;<br/>
                              $result = $totalUsage / $numberOfDays;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Cut Shirts</div>
                          <div class="description">The total number of shirts cut from the fabric within the specified period.</div>
                          <code>
                            $result = count(fabricRollLogEntries);
                          </code>
                        <br/>
                          <div class="description">
                            - counts <strong>fabric usage</strong>, not orders.<br/>
                            - it does <strong>not distinguish</strong> whether a fabric is used as Main, Contrast, or Cuff & Collar.<br/>
                            - a single shirt order may result in <strong>multiple fabric cuts</strong> during production (e.g. remakes or re-cuts),
                            so the same shirt can be counted multiple times (e.g. shirt #522507)
                          </div>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Used Fabric Per Cut Shirt</div>
                          <div class="description">The average length of fabric used per shirt cut.</div>
                          <code>
                            if(cutShirts != 0)<br>
                            $result = $totalUsage / $cutShirts;<br>
                            <br>
                            else<br>
                            $result = 0;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Delivered Amount</div>
                          <div class="description">The total length of fabric rolls delivered to the warehouse within the specified period.</div>
                          <code>
                            $result = $totalDeliveriesToWareHouse;
                          </code>
                      </div>
                      <div class="calculation-info-container">
                          <div class="title">Stock Change</div>
                          <div class="description">The difference between the total length of fabric used and the total length of fabric delivered to the warehouse within the specified period.</div>
                          <code>
                              $result = $totalUsage - $deliveredAmount;
                          </code>
                      </div>
                  </template>
                </FabricCalculationInfo>
              </span>
          </h3>
          <div class="row">
            <div class="col-md-3">
              <label>Date From</label>
              <datepicker
                :clear-button="true"
                placeholder="Date From"
                v-model="dateFrom"
                input-class="form-control"
              >
              </datepicker>
            </div>
            <div class="col-md-3">
              <label>Date To</label>
              <datepicker
                :clear-button="true"
                placeholder="Date To"
                v-model="dateTo"
                input-class="form-control"
              >
              </datepicker>
            </div>
            <div class="col-md-2">
              <label>Timezone</label>
              <v-select
                :options="timezones"
                v-model="selectedTimezone"
              ></v-select>
            </div>
          </div>
        </div>
      </div>
      <div class="row grid-body">
        <div class="col-md-6">
          <v-select
            taggable
            multiple
            v-model="selectedFabrics"
            pushTags
            :closeOnSelect="false"
            :options="allFabrics"
            placeholder="Filter by fabrics"
            label="production_name"
            :reduce="(allFabrics) => allFabrics.id"
          />
        </div>
        <div class="col-md-2">
          <button class="btn btn-block btn-success" @click="getAnalytics()">
            <i class="fa fa-search" aria-hidden="true"></i> Filter
          </button>
        </div>
        <div class="col-md-2">
          <button class="btn btn-block btn-success" @click="downloadStockData('normal')">
            <i class="fa fa-download" aria-hidden="true"></i> Download Stock Data
          </button>
        </div>
        <div class="col-md-2">
          <button class="btn btn-block btn-success" @click="downloadStockData('movement')">
            <i class="fa fa-download" aria-hidden="true"></i> Download Stock Movement Data
          </button>
        </div>
      </div>
      <div class="grid-body">
        <div class="table-responsive">
          <table border="1">
            <thead>
              <tr>
                <th @click="sort('id')">ID</th>
                <th @click="sort('name')">Name</th>
                <th @click="sort('total_stock')">Total stock</th>
                <th @click="sort('total_usage')">Total usage</th>
                <th @click="sort('avg_usage')">Avg. daily usage</th>
                <th @click="sort('shirt_cut_count')">Cut shirts</th>
                <th @click="sort('usage_per_cut')">
                  Used fabric per cut shirt
                </th>
                <th @click="sort('fabric_rank')">Rank</th>
                <th @click="sort('delivered_amount')">Delivered Amount</th>
                <th @click="sort('stock_change')">Stock change</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="fabric in fabrics">
                <tr>
                  <td>{{ fabric.id }}</td>
                  <td>{{ fabric.name }}</td>
                  <td>{{ fabric.total_stock.toFixed(2) }}</td>
                  <td>{{ fabric.total_usage.toFixed(2) }}</td>
                  <td>{{ fabric.avg_usage.toFixed(2) }}</td>
                  <td>{{ fabric.shirt_cut_count }}</td>
                  <td>{{ fabric.usage_per_cut.toFixed(2) }}</td>
                  <td>{{ fabric.fabric_rank }}</td>
                  <td>{{ fabric.delivered_amount.toFixed(2) }}</td>
                  <td>{{ fabric.stock_change.toFixed(2) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import pagination from "@/pages/Tools/pagination";
import AdvanceSearch from "../ShirtOrder/components/AdvanceSearch";
import NormalSearch from "../ShirtOrder/components/NormalSearch";
import FabricCalculationInfo from "./dashboardComponents/FabricCalculationInfo"

export default {
  components: {
    Datepicker,
    pagination,
    NormalSearch,
    AdvanceSearch,
    FabricCalculationInfo
  },
  data() {
    return {
      isLoading: false,
      dateFrom: null,
      dateTo: null,
      dateToday: null,
      selectedTimezone: null,
      fabrics: [],
      totalRows: 0,
      timezones: [
        { value: "UTC", label: "Server Time" },
        { value: "Europe/Berlin", label: "Germany" },
        { value: "Asia/Bangkok", label: "Thailand" },
      ],
      selectedFabrics: [],
      allFabrics: [],
      sortType: "asc",
    };
  },
  mounted() {
    /**
     * Initalize the time period to the last 30 days
     */
    this.dateFrom = new Date(
      new moment().startOf("today").subtract(30, "days")
    );
    this.dateTo = new Date(new moment().endOf("today"));
    this.dateToday = new Date(new moment().startOf("today"));
    this.selectedTimezone = this.timezones.filter(
      (tz) => tz.value == "Asia/Bangkok"
    )[0];
    this.getFabrics();
    this.getAnalytics();
  },
  methods: {
    async getFabrics() {
      let res = await axios.get("/v2/fabrics?as_dropdown=1");
      this.allFabrics = res.data.data.collection;
    },
    async getAnalytics() {
      if (this.dateFrom === null || this.dateTo === null) {
        Swal.fire({
          title: "ERROR",
          text: "Please select starting and ending time period",
          type: "error",
        });
        return;
      }

      // Format the dates to YYYY-MM-DD
      const dateFrom = this.dateFrom
        ? this.dateFrom.toISOString().split("T")[0]
        : null;
      const dateTo = this.dateTo
        ? this.dateTo.toISOString().split("T")[0]
        : null;
      const params = {
        fabrics: this.selectedFabrics,
        start: dateFrom,
        end: dateTo,
        timezone: this.selectedTimezone.value,
      };

      try {
        const response = await axios.get("/v2/fabrics/analytics", {
          params: params,
        });

        if (response.data.status === "OK") {
          const numberOfDays =
            moment(this.dateTo).diff(moment(this.dateFrom), "days") + 1;

          this.fabrics = response.data.data.map((fabric, index) => {
            fabric["shirt_cut_count"] = isNaN(fabric["shirt_cut_count"])
              ? 0
              : fabric["shirt_cut_count"];
            fabric["stock_change"] =
              fabric["total_usage"] - fabric["delivered_amount"];
            fabric["avg_usage"] = fabric["total_usage"] / numberOfDays;
            fabric["usage_per_cut"] =
              fabric["shirt_cut_count"] !== 0
                ? fabric["total_usage"] / fabric["shirt_cut_count"]
                : 0;
            return fabric;
          });
        } else {
          this.fabrics = [];
        }
      } catch (error) {
        this.fabrics = [];
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },
    async downloadStockData(type) {
      if (this.dateFrom === null || this.dateTo === null) {
        Swal.fire({
          title: "ERROR",
          text: "Please select starting and ending time period",
          type: "error",
        });
        return;
      }

      // Format the dates to YYYY-MM-DD
      const dateFrom = this.dateFrom
        ? this.dateFrom.toISOString().split("T")[0]
        : null;
      const dateTo = this.dateTo
        ? this.dateTo.toISOString().split("T")[0]
        : null;
      const params = {
        fabrics: this.selectedFabrics,
        start: dateFrom,
        end: dateTo,
        timezone: this.selectedTimezone.value,
      };

      try {

        const config = {
          'endpointUrl': (type === 'movement')
            ? '/v2/fabrics/download-stock-movement-data'
            : '/v2/fabrics/download-stock-data',
          'filenamePrefix': (type === 'movement')
            ? 'FabricStockMovement-'
            : 'FabricStock-'
        }

        const response = await axios.get(config.endpointUrl, {
          params: params,
          responseType: 'blob'
        });

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;

        let fileName =
            config.filenamePrefix + moment(dateFrom).format("YYYY-MM-DD")
              + '-' + moment(dateTo).format("YYYY-MM-DD");

        link.setAttribute("download", `${fileName}.xlsx`);
        document.body.appendChild(link);
        link.click();


      } catch (error) {
        console.log(error.response.data)
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },
    sort(column) {
      this.sortType = this.sortType === "asc" ? "desc" : "asc";
      this.fabrics = _.orderBy(this.fabrics, [column], [this.sortType]);
    },
  },
};
</script>

<style lang="scss" scoped>
th {
  text-align: center;
}
.mb-2 {
  margin-bottom: 5px;
}
h4 {
  margin-top: 7px;
}
table {
  table-layout: fixed !important;
  max-width: none;
  width: 100%;
  border-collapse: separate !important; /* Don't collapse */
  border-spacing: 0 !important;
}

th,
td {
  padding: 5px !important;
  border: 1px solid #d7d8da;
  border-left: none;
  border-top: none;
}
td:last-child,
th:last-child {
  border-right: none;
}

.table-responsive {
  height: 700px;
  overflow: scroll;
}
th {
  position: sticky;
  top: -1px;
  z-index: 1;
  background: var(--primary-color);
  color: white;
}
.fabric-calculation-info-container {
  display: inline-block;
  margin-left: 5px;
  padding: 6px;
  border-radius: 8px;
}
</style>
